'use client';

import SubmitButton from '@/components/app-ui/form/SubmitButton';
import TextInput from '@/components/app-ui/form/TextInput';
import { SignupDto } from '@/dtos/auth.dto';
import userApi from '@/services/user-api.service';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { INPUT_LIMITS, LEAD_SOURCES, PROFESSIONS, STUDENT_DEVICE_USAGE } from '@/constants/form.constants';
import CustomSelect from '@/components/app-ui/form/CustomSelect';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { gtmService } from '@/services/gtm.service';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export interface SsoSignupFormData {
	firstName: string;
	lastName: string;
	organization: string;
	profession: string;
	leadSource: string;
	studentDeviceUsage: string;
}

const schema = z.object({
	firstName: z.string().min(1, { message: 'First name is required' }),
	lastName: z.string().min(1, { message: 'Last name is required' }),
	organization: z.string().min(1, { message: 'Organization is required' }),
	profession: z.string().min(1, { message: 'Please select from the options' }),
	leadSource: z.string().min(1, { message: 'Please select from the options' }),
	studentDeviceUsage: z.string().min(1, { message: 'Please select from the options' }),
});

export default function SsoSignupForm() {
	const [isSigningUp, setIsSigningUp] = useState(false);
	const { data: session, update: sessionUpdate } = useSession();
	const [showFirstLastNameInputs, setShowFirstLastNameInputs] = useState(false);

	const ssoSignupForm = useForm<SsoSignupFormData>({
		defaultValues: {
			firstName: '',
			lastName: '',
			organization: '',
			profession: '',
			leadSource: '',
			studentDeviceUsage: '',
		},
		mode: 'onSubmit',
		resolver: zodResolver(schema),
	});
	const { handleSubmit, formState, control, setValue } = ssoSignupForm;
	const { errors } = formState;

	useEffect(() => {
		if (session?.signupUser) {
			const signupFirstName = session.signupUser.firstName;
			if (signupFirstName) setValue('firstName', signupFirstName);
			const signupLastName = session.signupUser.lastName;
			if (signupLastName) setValue('lastName', signupLastName);
			const signupOrganization = session.signupUser.organization;
			if (signupOrganization) setValue('organization', signupOrganization);

			// Show name inputs if signupUser doesn't have either firstName or lastName
			if (!signupFirstName || !signupLastName) {
				setShowFirstLastNameInputs(true);
			}
		}
	}, [session, setValue]);

	// const { executeRecaptcha } = useGoogleReCaptcha();

	const submitForm = async (data: SsoSignupFormData) => {
		// console.log('Form inputs', data);

		const signupUser = session?.signupUser;
		if (!signupUser) return toast.error('Error signing you up. Please try again.');

		const email = signupUser.email || '';
		const avatarUrl = signupUser.avatarUrl;
		const ssoType = signupUser.provider;

		// if (!executeRecaptcha) {
		// 	toast.error('Recaptcha not loaded. Please refresh the page and try again.');
		// 	return;
		// }

		// setIsSigningUp(true);
		// const token = await executeRecaptcha('sso_signup');
		// if (!token) {
		// 	setIsSigningUp(false);
		// 	toast.error('Recaptcha not loaded. Please refresh the page and try again.');
		// 	return;
		// }

		setIsSigningUp(true);
		const signupDto: SignupDto = {
			firstName: data.firstName.trim(),
			lastName: data.lastName.trim(),
			email: email.trim().toLowerCase(),
			avatarUrl,
			ssoType,
			organization: data.organization.trim(),
			jobRole: data.profession,
			leadSource: data.leadSource,
			signupSurvey: {
				studentDeviceUsage: data.studentDeviceUsage,
			},
			token: '',
		};
		// console.log('signupDto', signupDto);
		const userId = await userApi.createUser(signupDto);
		if (!userId) return toast.error('Error signing you up. Please try again.');
		else {
			sessionUpdate(); // this will trigger jwt and session callback to invoke
			gtmService.sendSignUpEvent(userId, ssoType);
			return;
		}
	};

	return (
		<>
			<form noValidate onSubmit={handleSubmit(submitForm)}>
				<div className="mx-auto max-w-xl space-y-3 lg:mr-0 lg:max-w-lg">
					{showFirstLastNameInputs && (
						<div className="grid grid-cols-1 gap-x-4 space-y-3 xxs:gap-x-6 xs:grid-cols-2 xs:space-y-0">
							<TextInput
								name="firstName"
								label="First name"
								maxLength={INPUT_LIMITS.FIRST_LAST_NAME_MAX}
								control={control}
								errorMessage={errors.firstName?.message}
							/>
							<TextInput
								name="lastName"
								label="Last name"
								maxLength={INPUT_LIMITS.FIRST_LAST_NAME_MAX}
								control={control}
								errorMessage={errors.lastName?.message}
							/>
						</div>
					)}
					<TextInput
						name="organization"
						label="Organization"
						maxLength={INPUT_LIMITS.ORGANIZATION_MAX}
						control={control}
						errorMessage={errors.organization?.message}
					/>
					<>
						<CustomSelect
							name="profession"
							label="Your profession"
							options={PROFESSIONS}
							defaultValue={PROFESSIONS[0]}
							control={control}
							errorMessage={errors.profession?.message}
						/>
						<CustomSelect
							name="leadSource"
							label="How did you hear about ClassPoint?"
							options={LEAD_SOURCES.slice(1).sort(() => Math.random() - 0.5)}
							defaultValue={LEAD_SOURCES[0]}
							control={control}
							errorMessage={errors.leadSource?.message}
						/>
						<CustomSelect
							name="studentDeviceUsage"
							label="Use of student devices in your classroom"
							options={STUDENT_DEVICE_USAGE}
							defaultValue={STUDENT_DEVICE_USAGE[0]}
							control={control}
							errorMessage={errors.studentDeviceUsage?.message}
						/>
					</>
				</div>
				<div className="mt-6 space-y-3">
					<SubmitButton fullWidth isSubmitting={isSigningUp}>
						Finish signing up
					</SubmitButton>
				</div>
			</form>
		</>
	);
}
